import {Injectable} from '@angular/core';
import {RestApiService} from './rest-api.service';
import {Router} from '@angular/router';
import {DomSanitizer} from '@angular/platform-browser';
import {RoleCollectionRes, RoleRes, UserDetailsRes, VersionInfoRes} from '../contracts/v0/cp';
import {CookieType} from '../models/cp-auth-types';
import {CookieService} from 'ngx-cookie-service';
import {SettingsDetailRes} from '../contracts/v0/crm/settings.contract';
import {BaseCollectionModel, YascRuntimeCacheService} from '@yasc/yasc-material-components';
import {L10nTranslationService} from 'angular-l10n';
import {BehaviorSubject, catchError, finalize, firstValueFrom, map, throwError} from 'rxjs';
import {ProjectMinRes, ProjectTrackerRes} from '../contracts/v0/crm';

@Injectable({
    providedIn: 'root'
})
export class RuntimeCacheService extends YascRuntimeCacheService {
    loginInfo?: UserDetailsRes;
    supportedCultures?: string[];
    userSettings?: SettingsDetailRes;
    projects?: ProjectMinRes[];
    version?: VersionInfoRes;

    private _token: string | null = null;
    refreshToken?: string;


    private permissionsRequest?: Promise<RoleCollectionRes>;
    private userSettingsRequest?: Promise<SettingsDetailRes>;


    private _allRights: RoleRes[] = [];
    allRightsSet = new BehaviorSubject<RoleRes[]>([]);

    permissionsLoaded$ = new BehaviorSubject<boolean>(false);
    userSettingsLoaded$ = new BehaviorSubject<boolean>(false);


    override userRights: RoleCollectionRes = new RoleCollectionRes();
    ongoingBugScreenshot: boolean = false;

    constructor(
        restApi: RestApiService,
        sanitizer: DomSanitizer,
        private router: Router,
        public translation: L10nTranslationService,
        private cookieService: CookieService,
    ) {
        super(restApi, sanitizer);
        this.resetUserRights();
    }

    get allRights(): RoleRes[] {
        return this._allRights;
    }

    set allRights(value: RoleRes[]) {
        this._allRights = value ?? [];
        this.allRightsSet.next(this._allRights);
    }

    set token(value: string) {
        const normalized = value?.trim() ?? '';
        this._token = normalized;

        if (normalized) {
            this.cookieService.set(CookieType.UserToken, normalized, {path: '/'});
        } else {
            this.cookieService.delete(CookieType.UserToken, '/');
        }
    }

    get token(): string {
        if (this._token === null) {
            this._token = this.cookieService.get(CookieType.UserToken) ?? '';
        }
        return this._token;
    }

    isLogged(): boolean {
        return this.token.trim().length > 0;
    }

    hasPermission(permission: string): boolean {
        return this.userRights.permissions.includes(permission.toLowerCase());
    }

    private applyUserDetails(val: UserDetailsRes): RoleCollectionRes {
        this.loginInfo = val;
        this.userRights = new RoleCollectionRes();
        this.userRights.roles = val.roles ?? [];
        this.userRights.permissions = (val.permissions ?? []).map(e => e.name.toLowerCase());
        this.permissionsLoaded$.next(true);
        return this.userRights;
    }

    private applyUserSettings(val: SettingsDetailRes): SettingsDetailRes {
        this.userSettings = val;
        return this.userSettings;
    }

    private resetUserRights(): void {
        this.userRights = new RoleCollectionRes();
        this.userRights.roles = [];
        this.userRights.permissions = [];
        this.permissionsLoaded$.next(false);
    }

    resetAuthState(): void {
        this._token = '';
        this.refreshToken = undefined;
        this.permissionsRequest = undefined;

        this.loginInfo = undefined;
        this.userSettings = undefined;
        this.allRights = [];
        this.resetUserRights();

        this.cookieService.delete(CookieType.UserToken, '/');
    }

    logout(redirectToLogin: boolean = true): void {
        this.resetAuthState();

        if (redirectToLogin) {
            this.router.navigate(['auth/login']).then();
        }
    }

    async initUserContext(forceReload = false): Promise<void> {
        if (!this.isLogged()) {
            this.resetUserRights();
            return;
        }

        await this.loadPermissions(forceReload);
        await this.loadUserSettings();
    }

    async loadUserSettings() {
        if (!this.isLogged()) {
            this.resetUserRights();
            throw new Error('NOT_AUTHENTICATED');
        }

        if (this.userSettings) {
            this.userSettingsLoaded$.next(true);
            return this.userSettings;
        }

        if (this.userSettingsRequest) {
            return this.userSettingsRequest;
        }

        this.userSettingsRequest = firstValueFrom(
            this.restApi.v0.crm.user.getSettings().pipe(
                map((val: SettingsDetailRes) => this.applyUserSettings(val)),
                catchError((err) => {
                    this.userSettingsLoaded$.next(false);
                    if (err?.status === 401) {
                        this.resetAuthState();
                    }
                    return throwError(() => err);
                }),
                finalize(() => {
                    this.userSettingsRequest = undefined;
                })
            )
        );

        return this.userSettingsRequest;


    }

    async loadPermissions(forceReload = false): Promise<RoleCollectionRes> {
        if (!this.isLogged()) {
            this.resetUserRights();
            throw new Error('NOT_AUTHENTICATED');
        }

        if (!forceReload && this.userRights.permissions.length > 0) {
            this.permissionsLoaded$.next(true);
            return this.userRights;
        }

        if (this.permissionsRequest) {
            return this.permissionsRequest;
        }

        this.permissionsRequest = firstValueFrom(
            this.restApi.v0.accounts.details().pipe(
                map((val: UserDetailsRes) => this.applyUserDetails(val)),
                catchError((err) => {
                    this.permissionsLoaded$.next(false);
                    if (err?.status === 401) {
                        this.resetAuthState();
                    }
                    return throwError(() => err);
                }),
                finalize(() => {
                    this.permissionsRequest = undefined;
                })
            )
        );

        return this.permissionsRequest;
    }
}